import { useEffect, useRef, useCallback, useState } from "react";
import { api } from "./api";
import { logger } from "./logger";
import {
  ConnectionStatus,
  type ConnectionStatusType,
  useWebSocketRefs,
  useHeartbeat,
  canConnect,
  buildWebSocketUrl,
  getReconnectDelay,
  clearReconnectTimeout,
} from "./useWebSocketBase";
import { wsRegistry } from "./wsRegistry";
import { useAuthStore } from "../store/authStore";
import { useGameStore } from "../store/gameStore";
import {
  ClientActionEnum,
  ServerActionEnum,
  type WebSocketMessage,
} from "../types/game";

// Re-export for backward compatibility
export { ConnectionStatus };
export type { ConnectionStatusType };

interface UseWebSocketOptions {
  sessionId: string;
  playerId: string;
  playerToken: string;
  onMessage?: (message: WebSocketMessage) => void;
  onError?: (error: Event) => void;
  onClose?: () => void;
  onOpen?: () => void;
  onFatalError?: (code: number, reason: string) => void;
}

export function useWebSocket({
  sessionId,
  playerId,
  playerToken,
  onMessage,
  onError,
  onClose,
  onOpen,
  onFatalError,
}: UseWebSocketOptions) {
  // Use shared refs from base hook
  const {
    wsRef,
    reconnectTimeout,
    reconnectAttempts,
    heartbeatIntervalRef,
    missedPongsRef,
    connectRef,
  } = useWebSocketRefs();
  const [status, setStatus] = useState<ConnectionStatusType>(
    ConnectionStatus.DISCONNECTED,
  );
  const maxReconnectAttempts = 10;
  const authRetryAttempts = useRef(0);
  const maxAuthRetries = 2; // Try to refresh token up to 2 times

  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  const onCloseRef = useRef(onClose);
  const onOpenRef = useRef(onOpen);
  const onFatalErrorRef = useRef(onFatalError);

  // Keep refs updated with latest callbacks to avoid stale closure issues
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);
  useEffect(() => {
    onFatalErrorRef.current = onFatalError;
  }, [onFatalError]);

  const sendMessage = useCallback(
    (
      action: ClientActionEnum | string,
      payload: Record<string, unknown> = {},
    ) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const message = { action, payload };
        if (action !== ClientActionEnum.PING) {
          logger.debug("websocket_send_message", { action });
        }
        wsRef.current.send(JSON.stringify(message));
      } else {
        logger.warn("websocket_send_failed_not_connected", { action });
      }
    },
    [wsRef],
  );

  // Use shared heartbeat management
  const { startHeartbeat, stopHeartbeat, handlePong } = useHeartbeat(
    wsRef,
    heartbeatIntervalRef,
    missedPongsRef,
    sendMessage,
  );

  const connect = useCallback(() => {
    if (
      !sessionId ||
      !playerId ||
      !playerToken ||
      typeof window === "undefined"
    ) {
      return;
    }
    if (!canConnect(wsRef)) {
      return;
    }

    try {
      setStatus(ConnectionStatus.CONNECTING);

      const url = buildWebSocketUrl(`/ws/${sessionId}/${playerId}`);
      logger.debug("websocket_connecting_via_proxy", { url });

      wsRef.current = new WebSocket(url, [playerToken]);

      wsRef.current.onopen = () => {
        logger.info("websocket_connected", { sessionId, playerId });
        setStatus(ConnectionStatus.CONNECTED);
        reconnectAttempts.current = 0;
        authRetryAttempts.current = 0; // Reset auth retries on successful connection
        startHeartbeat();
        const lastSeenSequence = useGameStore.getState().lastSeenSequence;
        const readyMessage = {
          action: ClientActionEnum.CLIENT_READY,
          payload: { last_seen_sequence_id: lastSeenSequence },
        };
        wsRef.current?.send(JSON.stringify(readyMessage));
        onOpenRef.current?.();
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          if (message.action === ServerActionEnum.PONG) {
            handlePong();
            return;
          }
          onMessageRef.current?.(message);
        } catch (error) {
          logger.error("websocket_parse_error", { error: String(error) });
        }
      };

      wsRef.current.onerror = (error) => {
        logger.error("websocket_error", {
          url,
          readyState: wsRef.current?.readyState,
        });
        setStatus(ConnectionStatus.ERROR);
        onErrorRef.current?.(error);
      };

      wsRef.current.onclose = async (event) => {
        if (!wsRef.current) {
          // This means disconnect() was called, so it's an intentional closure.
          logger.debug("websocket_closed_intentional");
          return;
        }

        logger.debug("websocket_closed_unexpected", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });
        setStatus(ConnectionStatus.DISCONNECTED);
        stopHeartbeat();
        onCloseRef.current?.();

        // Handle 4003 (auth required) with retry - try to get fresh token
        if (event.code === 4003 && authRetryAttempts.current < maxAuthRetries) {
          authRetryAttempts.current++;
          logger.info("websocket_auth_retry", {
            attempt: authRetryAttempts.current,
            maxAttempts: maxAuthRetries,
          });

          try {
            // Try to rejoin the room to get a fresh token
            const accessToken = useAuthStore.getState().accessToken;
            const displayName = useGameStore.getState().displayName || "Player";

            if (accessToken && sessionId) {
              const response = await api.joinRoom({
                session_id: sessionId,
                display_name: displayName,
              });
              if (response.player_token) {
                // Update token and reconnect
                useGameStore.getState().setPlayerToken(response.player_token);
                logger.info("websocket_auth_token_refreshed", { sessionId });
                // Reconnect with fresh token after short delay
                reconnectTimeout.current = setTimeout(
                  () => connectRef.current(),
                  500,
                );
                return;
              }
            }
          } catch (error) {
            logger.warn("websocket_auth_retry_failed", {
              error: String(error),
            });
          }
        }

        // Fatal errors that should not retry
        const fatalErrorCodes = [1008, 4004]; // Removed 4003 - handled above
        if (fatalErrorCodes.includes(event.code)) {
          logger.error("websocket_fatal_error", {
            code: event.code,
            reason: event.reason,
          });
          onFatalErrorRef.current?.(event.code, event.reason);
          return; // Do not reconnect on fatal errors
        }

        // If 4003 and exhausted retries, treat as fatal
        if (
          event.code === 4003 &&
          authRetryAttempts.current >= maxAuthRetries
        ) {
          logger.error("websocket_auth_retries_exhausted", {
            maxAttempts: maxAuthRetries,
          });
          onFatalErrorRef.current?.(event.code, event.reason);
          return;
        }

        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = getReconnectDelay(reconnectAttempts.current, 10000);
          logger.info("websocket_reconnecting", {
            delay,
            attempt: reconnectAttempts.current,
            maxAttempts: maxReconnectAttempts,
          });
          reconnectTimeout.current = setTimeout(
            () => connectRef.current(),
            delay,
          );
        } else {
          logger.error("websocket_max_reconnects_reached", {
            maxAttempts: maxReconnectAttempts,
          });
          onFatalErrorRef.current?.(0, "Max reconnection attempts reached");
        }
      };
    } catch (error) {
      logger.error("websocket_create_failed", { error: String(error) });
      setStatus(ConnectionStatus.ERROR);
    }
  }, [
    sessionId,
    playerId,
    playerToken,
    startHeartbeat,
    stopHeartbeat,
    wsRef,
    reconnectAttempts,
    handlePong,
    connectRef,
    reconnectTimeout,
  ]);

  useEffect(() => {
    connectRef.current = connect;
  });

  const disconnect = useCallback(() => {
    clearReconnectTimeout(reconnectTimeout);
    stopHeartbeat();
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setStatus(ConnectionStatus.DISCONNECTED);
  }, [reconnectTimeout, stopHeartbeat, wsRef]);

  useEffect(() => {
    if (sessionId && playerId && playerToken) {
      // Intentional connection setup - not a cascading render issue

      // eslint-disable-next-line react-hooks/set-state-in-effect
      connect();
    }
    return () => {
      disconnect();
    };
  }, [sessionId, playerId, playerToken, connect, disconnect]);

  useEffect(() => {
    let didRegister = false;
    if (
      status === ConnectionStatus.CONNECTED &&
      sessionId &&
      playerId &&
      sendMessage
    ) {
      wsRegistry.register(sessionId, playerId, sendMessage);
      didRegister = true;
    }
    return () => {
      if (didRegister && sessionId) {
        wsRegistry.unregister(sessionId);
      }
    };
  }, [status, sessionId, playerId, sendMessage]);

  return {
    status,
    sendMessage,
    connect,
    disconnect,
  };
}
